// Dropdown that lists the currently-hidden widgets for the active
// tab and re-adds them on click. Hidden = layout entries with
// visible:false. Clicking flips visible:true via the parent's
// onAdd callback. The parent owns state -- this is a dumb menu.
//
// Lives next to the Reset button in the page header. When there's
// nothing hidden, the button is disabled with a helpful tooltip so
// it's discoverable but not confusing.

import React, { useEffect, useRef, useState } from 'react'
import { isEmpty } from 'lodash'
import { IconPlus } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { Pill } from '@/components/pills/Pill'

export const AddWidgetMenu = ({ hiddenWidgets, onAdd }) => {
  const [open, setOpen] = useState(false)
  const containerRef = useRef(null)
  const hasHidden = !isEmpty(hiddenWidgets)

  // Close on outside-click. Capture phase so an inner click doesn't
  // bubble up and re-trigger the close. We bind only while open to
  // keep the listener count minimal.
  useEffect(() => {
    if (!open) {
      return undefined
    }
    const handleDocClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false)
      }
    }
    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleDocClick)
    document.addEventListener('keydown', handleEsc)
    return () => {
      document.removeEventListener('mousedown', handleDocClick)
      document.removeEventListener('keydown', handleEsc)
    }
  }, [open])

  // Group hidden widgets by section so the menu reads like the
  // dashboard's own narrative (At a glance / Over time / ...).
  const grouped = hiddenWidgets.reduce((acc, item) => {
    const sectionKey = item.section || 'other'
    if (!acc[sectionKey]) {
      acc[sectionKey] = { sectionName: item.sectionName, items: [] }
    }
    acc[sectionKey].items.push(item)
    return acc
  }, {})

  return (
    <div className="add-widget-menu" ref={containerRef}>
      <Button
        basic
        onClick={() => hasHidden && setOpen((prev) => !prev)}
        disabled={!hasHidden}
        title={hasHidden ? 'Add a hidden widget back to this tab' : 'No hidden widgets on this tab'}
      >
        <IconPlus /> Add widget
        {hasHidden && (
          <Pill brand xs bold className="add-widget-menu-count" content={hiddenWidgets.length} />
        )}
      </Button>
      {open && hasHidden && (
        <div className="add-widget-menu-pop" role="menu">
          {Object.entries(grouped).map(([sectionKey, group]) => (
            <div key={sectionKey} className="add-widget-menu-group">
              <div className="add-widget-menu-group-label">{group.sectionName}</div>
              {group.items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="menuitem"
                  className="add-widget-menu-item"
                  onClick={() => {
                    onAdd(item.id)
                    setOpen(false)
                  }}
                >
                  <span className="add-widget-menu-item-label">{item.displayName}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
